import type {
  SaveStockTransferRequest,
  StockTransferRecord,
} from '@/renderer/types/inventory';

export interface LineForm {
  key: string;
  inventoryItemId: string;
  quantity: string;
}

export interface FormState {
  fromLocationId: string;
  toLocationId: string;
  transferDate: string;
  reference: string;
  notes: string;
  lines: LineForm[];
}

export const TRANSFER_STATUS_LABELS: Record<
  StockTransferRecord['status'],
  string
> = {
  draft: 'Draft',
  posted: 'Posted',
  cancelled: 'Cancelled',
};

export const TRANSFER_STATUS_COLORS: Record<
  StockTransferRecord['status'],
  string
> = {
  draft: 'bg-yellow-100 text-yellow-700',
  posted: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

export const makeStockTransferLineKey = () =>
  Math.random().toString(36).slice(2);

export const createEmptyStockTransferLine = (): LineForm => ({
  key: makeStockTransferLineKey(),
  inventoryItemId: '',
  quantity: '',
});

export const buildInitialStockTransferFormState = (
  transfer: StockTransferRecord | null,
  today: string,
): FormState => ({
  fromLocationId: transfer?.from_stock_location_id ?? '',
  toLocationId: transfer?.to_stock_location_id ?? '',
  transferDate: transfer?.transfer_date ?? today,
  reference: transfer?.reference ?? '',
  notes: transfer?.notes ?? '',
  lines: transfer?.lines?.map((l) => ({
    key: l.id,
    inventoryItemId: l.inventory_item_id,
    quantity: String(l.quantity),
  })) ?? [createEmptyStockTransferLine()],
});

export interface LineAvailability {
  hasBalanceData: boolean;
  availableQty: number;
  requestedQty: number;
  isOverAvailable: boolean;
  hasQuantityError: boolean;
}

export const getLineAvailability = (
  line: LineForm,
  balanceByItemId: Map<string, number>,
  fromLocationId: string,
): LineAvailability => {
  const hasBalanceData = balanceByItemId.has(line.inventoryItemId);
  const availableQty = balanceByItemId.get(line.inventoryItemId) ?? 0;
  const requestedQty = Number(line.quantity);
  const isOverAvailable =
    Boolean(fromLocationId) &&
    hasBalanceData &&
    requestedQty > 0 &&
    requestedQty > availableQty;
  const hasQuantityError = Boolean(line.inventoryItemId) && !(requestedQty > 0);

  return {
    hasBalanceData,
    availableQty,
    requestedQty,
    isOverAvailable,
    hasQuantityError,
  };
};

export const hasAnyInvalidQuantity = (lines: LineForm[]) =>
  lines.some((l) => Boolean(l.inventoryItemId) && !(Number(l.quantity) > 0));

export const hasAnyOverAvailable = (
  lines: LineForm[],
  balanceByItemId: Map<string, number>,
  fromLocationId: string,
) =>
  lines.some(
    (l) =>
      getLineAvailability(l, balanceByItemId, fromLocationId).isOverAvailable,
  );

export const canSubmitStockTransferForm = (
  form: FormState,
  balanceByItemId: Map<string, number>,
): boolean =>
  Boolean(
    form.fromLocationId &&
    form.toLocationId &&
    form.fromLocationId !== form.toLocationId &&
    form.lines.some((l) => l.inventoryItemId) &&
    !hasAnyInvalidQuantity(form.lines) &&
    !hasAnyOverAvailable(form.lines, balanceByItemId, form.fromLocationId),
  );

export const buildSaveStockTransferRequest = (
  form: FormState,
  ctx: { transferId?: string; companyId: number; outletId: string },
): SaveStockTransferRequest => {
  const validLines = form.lines.filter(
    (l) => l.inventoryItemId && Number(l.quantity) > 0,
  );

  return {
    id: ctx.transferId,
    companyId: ctx.companyId,
    outletId: ctx.outletId,
    fromStockLocationId: form.fromLocationId,
    toStockLocationId: form.toLocationId,
    transferDate: form.transferDate,
    reference: form.reference || null,
    notes: form.notes || null,
    lines: validLines.map((l) => ({
      inventoryItemId: l.inventoryItemId,
      quantity: Number(l.quantity),
    })),
  };
};

export const getRowItemOptions = <T extends { value: string }>(
  allOptions: T[],
  lines: LineForm[],
  currentLineKey: string,
): (T & { disabled?: boolean })[] => {
  const currentLine = lines.find((l) => l.key === currentLineKey);
  const otherSelectedIds = new Set(
    lines
      .filter((l) => l.key !== currentLineKey)
      .map((l) => l.inventoryItemId)
      .filter(Boolean),
  );

  return allOptions.map((o) => ({
    ...o,
    disabled:
      o.value !== currentLine?.inventoryItemId && otherSelectedIds.has(o.value),
  }));
};
